"use client";

import { useQuery } from "@tanstack/react-query";
import {
  Bell,
  ChevronDown,
  ChevronRight,
  FileText,
  LogOut,
  Menu,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Settings,
  Sun,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  useDeferredValue,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import { toPanelUser } from "@/lib/adapters/auth";
import { toDashboard } from "@/lib/adapters/operations";
import { apiRequest, resetCsrf } from "@/lib/api/client";
import { authStateSchema } from "@/lib/schemas/auth";
import { dashboardSchema } from "@/lib/schemas/operations";
import type { PanelUser } from "@/lib/domain/models";
import { PANEL_ROUTE_ICONS } from "@/lib/panel-navigation";
import { Breadcrumbs, type BreadcrumbItem } from "./ui/breadcrumbs";
import { IconButton } from "./ui/controls";
import {
  Dialog,
  Drawer,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  DropdownSeparator,
  Tooltip,
} from "./ui/overlays";
import { Skeleton } from "./ui/states";
import { useToast } from "./ui/toast-context";

type NavigationItem = {
  href: string;
  label: string;
  icon: LucideIcon;
  capability?: string;
  badgeKey?: string;
};

type NavigationGroup = {
  id: string;
  label: string;
  items: NavigationItem[];
};

const navigationGroups: NavigationGroup[] = [
  {
    id: "general",
    label: "Genel",
    items: [{ href: "/dashboard", label: "Dashboard", icon: PANEL_ROUTE_ICONS["/dashboard"] }],
  },
  {
    id: "publishing",
    label: "Yayın",
    items: [
      {
        href: "/icerik",
        label: "İş akışı",
        icon: PANEL_ROUTE_ICONS["/icerik"],
        badgeKey: "workflow",
      },
    ],
  },
  {
    id: "content",
    label: "İçerik",
    items: [
      { href: "/anasayfa", label: "Ana sayfa", icon: PANEL_ROUTE_ICONS["/anasayfa"] },
      { href: "/hero", label: "Hero", icon: PANEL_ROUTE_ICONS["/hero"] },
      { href: "/sayfalar", label: "Sayfalar & Metinler", icon: PANEL_ROUTE_ICONS["/sayfalar"] },
      { href: "/hizmetler", label: "Hizmetler", icon: PANEL_ROUTE_ICONS["/hizmetler"] },
      { href: "/referanslar", label: "Referanslar", icon: PANEL_ROUTE_ICONS["/referanslar"] },
      { href: "/finansman", label: "Finansman", icon: PANEL_ROUTE_ICONS["/finansman"] },
      { href: "/karsilastirmalar", label: "Karşılaştırmalar", icon: PANEL_ROUTE_ICONS["/karsilastirmalar"] },
      { href: "/blog", label: "Blog", icon: PANEL_ROUTE_ICONS["/blog"] },
      { href: "/egitim", label: "Eğitim", icon: PANEL_ROUTE_ICONS["/egitim"] },
      { href: "/kurumsal", label: "Kurumsal", icon: PANEL_ROUTE_ICONS["/kurumsal"] },
      {
        href: "/firsatlar",
        label: "Fırsatlar",
        icon: PANEL_ROUTE_ICONS["/firsatlar"],
        capability: "opportunities.view",
        badgeKey: "opportunities",
      },
      {
        href: "/medya",
        label: "Medya",
        icon: PANEL_ROUTE_ICONS["/medya"],
        capability: "media.view",
      },
    ],
  },
  {
    id: "operations",
    label: "Operasyon",
    items: [
      {href:"/firsat-talepleri",label:"Fırsat talepleri",icon:PANEL_ROUTE_ICONS["/basvurular"],capability:"applications.view"},
      {
        href: "/basvurular",
        label: "Başvurular",
        icon: PANEL_ROUTE_ICONS["/basvurular"],
        capability: "applications.view",
        badgeKey: "submissions",
      },
      {
        href: "/uyeler",
        label: "Üyeler",
        icon: PANEL_ROUTE_ICONS["/uyeler"],
        capability: "members.view",
        badgeKey: "members",
      },
      {
        href: "/bulten",
        label: "Bülten",
        icon: PANEL_ROUTE_ICONS["/bulten"],
        capability: "newsletter.view",
      },
    ],
  },
  {
    id: "administration",
    label: "Yönetim",
    items: [
      {
        href: "/kullanicilar",
        label: "Kullanıcılar",
        icon: PANEL_ROUTE_ICONS["/kullanicilar"],
        capability: "users.view",
      },
      {
        href: "/roller",
        label: "Roller",
        icon: PANEL_ROUTE_ICONS["/roller"],
        capability: "users.view",
      },
      {
        href: "/denetim",
        label: "Denetim",
        icon: PANEL_ROUTE_ICONS["/denetim"],
        capability: "audit.view",
      },
      {
        href: "/cop-kutusu",
        label: "Çöp kutusu",
        icon: PANEL_ROUTE_ICONS["/cop-kutusu"],
        capability: "content.view",
      },
      {
        href: "/ayarlar",
        label: "Ayarlar",
        icon: PANEL_ROUTE_ICONS["/ayarlar"],
        capability: "settings.manage",
      },
    ],
  },
];

const searchResultSchema = z.array(
  z.object({
    id: z.string(),
    kind: z.string(),
    title: z.string(),
    path: z.string(),
  }),
);

const breadcrumbNames: Record<string, string> = Object.fromEntries(
  navigationGroups.flatMap((group) => group.items.map((item) => [item.href, item.label])),
);
breadcrumbNames["/bildirimler"] = "Bildirimler";
breadcrumbNames["/editorial"] = "Editoryal içerik";

function isRouteActive(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toLocaleUpperCase("tr-TR"))
    .join("") || "IP";
}

function getBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const base = Object.keys(breadcrumbNames)
    .sort((a, b) => b.length - a.length)
    .find((href) => isRouteActive(pathname, href));
  if (!base) return [{ label: "Panel", href: "/dashboard" }];
  const items: BreadcrumbItem[] = [{ label: "Panel", href: "/dashboard" }];
  const label = breadcrumbNames[base];
  if (pathname === base) items.push({ label });
  else items.push({ label, href: base }, { label: "Düzenle" });
  return items;
}

function SidebarNavigation({
  pathname,
  groups,
  collapsedGroups,
  badgeValues,
  idPrefix,
  onToggleGroup,
  onNavigate,
}: {
  pathname: string;
  groups: NavigationGroup[];
  collapsedGroups: Set<string>;
  badgeValues: Record<string, number>;
  idPrefix: string;
  onToggleGroup: (id: string) => void;
  onNavigate?: () => void;
}) {
  return (
    <nav
      className="sidebar-navigation"
      id={`${idPrefix}-panel-navigation`}
      aria-label="Panel menüsü"
    >
      {groups.map((group) => {
        const collapsed = collapsedGroups.has(group.id);
        return (
          <section
            className={`nav-group ${collapsed ? "is-collapsed" : ""}`}
            key={group.id}
          >
            <button
              type="button"
              className="nav-group-toggle"
              aria-expanded={!collapsed}
              aria-controls={`${idPrefix}-nav-group-${group.id}`}
              onClick={() => onToggleGroup(group.id)}
            >
              <span>{group.label}</span>
              <ChevronDown size={14} aria-hidden="true" />
            </button>
            <div className="nav-items" id={`${idPrefix}-nav-group-${group.id}`}>
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = isRouteActive(pathname, item.href);
                const badge = item.badgeKey ? badgeValues[item.badgeKey] : 0;
                const link = (
                  <Link
                    className={`nav-link ${active ? "active" : ""}`}
                    to={item.href}
                    aria-label={item.label}
                    aria-current={active ? "page" : undefined}
                    onClick={onNavigate}
                  >
                    <Icon size={18} aria-hidden="true" />
                    <span className="nav-link-label">{item.label}</span>
                    {!!badge && <span className="nav-count">{badge > 99 ? "99+" : badge}</span>}
                  </Link>
                );
                return (
                  <Tooltip label={item.label} key={item.href}>
                    {link}
                  </Tooltip>
                );
              })}
            </div>
          </section>
        );
      })}
    </nav>
  );
}

function SidebarBrand({ onClose }: { onClose?: () => void }) {
  return (
    <div className="brand-lockup sidebar-brand">
      <img className="brand-logo-mark" src="/ipf-logo.png" alt="" width="36" height="38" />
      <span className="brand-copy"><strong>IPF</strong><small>Yönetim Paneli</small></span>
      {onClose && (
        <IconButton label="Menüyü kapat" variant="ghost" onClick={onClose}>
          <X size={19} />
        </IconButton>
      )}
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const auth = useQuery({
    queryKey: ["session"],
    queryFn: async () => (await apiRequest("auth/session/", authStateSchema)).data,
  });

  useEffect(() => {
    if (!auth.isLoading && (!auth.data || auth.data.stage !== "authenticated")) {
      navigate("/giris", { replace: true });
    }
  }, [auth.data, auth.isLoading, navigate]);

  if (auth.isLoading) {
    return (
      <div className="shell-loading">
        <Skeleton lines={4} label="Panel hazırlanıyor" />
      </div>
    );
  }
  if (!auth.data || auth.data.stage !== "authenticated") return null;

  return (
    <AuthenticatedShell user={toPanelUser(auth.data.user)}>
      {children}
    </AuthenticatedShell>
  );
}

function readCollapsedGroups(userId: string) {
  try {
    const value = JSON.parse(
      window.localStorage.getItem(`ipf-panel-nav:${userId}`) ?? "[]",
    );
    if (Array.isArray(value)) {
      return new Set<string>(value.filter((item) => typeof item === "string"));
    }
  } catch {
    // Invalid device preference falls back to the complete navigation.
  }
  return new Set<string>();
}

function readSidebarCollapsed(userId: string) {
  try {
    return window.localStorage.getItem(`ipf-panel-sidebar:${userId}`) === "collapsed";
  } catch {
    return false;
  }
}

function readThemePreference() {
  try {
    const stored = window.localStorage.getItem("ipf-panel-theme");
    if (stored === "dark" || stored === "light") return stored;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch {
    return "light";
  }
}

function AuthenticatedShell({
  user,
  children,
}: {
  user: PanelUser;
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const notify = useToast();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeResult, setActiveResult] = useState(0);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() =>
    readCollapsedGroups(user.id),
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    readSidebarCollapsed(user.id),
  );
  const [theme, setTheme] = useState<"light" | "dark">(() => readThemePreference());
  const deferredQuery = useDeferredValue(query.trim());
  const preferenceKey = `ipf-panel-nav:${user.id}`;

  const overview = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () =>
      toDashboard((await apiRequest("dashboard/", dashboardSchema)).data),
  });
  const search = useQuery({
    queryKey: ["search", deferredQuery],
    enabled: paletteOpen && deferredQuery.length >= 2,
    queryFn: async () =>
      (
        await apiRequest(
          `search/?q=${encodeURIComponent(deferredQuery)}`,
          searchResultSchema,
        )
      ).data,
  });

  useEffect(() => {
    function keyboard(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPaletteOpen(true);
      }
    }
    document.addEventListener("keydown", keyboard);
    return () => document.removeEventListener("keydown", keyboard);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", theme === "dark" ? "#07131f" : "#eef3f8");
    try {
      window.localStorage.setItem("ipf-panel-theme", theme);
    } catch {
      // Tema tercihi yalnız mevcut oturum boyunca korunur.
    }
  }, [theme]);


  function allowed(item: NavigationItem) {
    return !item.capability || user.capabilities.has(item.capability);
  }

  const visibleGroups = navigationGroups
    .map((group) => ({ ...group, items: group.items.filter(allowed) }))
    .filter((group) => group.items.length > 0);

  const cards = overview.data?.cards ?? [];
  const cardsByKey = Object.fromEntries(cards.map((card) => [card.key, card.value]));
  const badgeValues = {
    workflow: (cardsByKey.review ?? 0) + (cardsByKey.changes ?? 0),
    submissions: cardsByKey.submissions ?? 0,
    members: cardsByKey.members ?? 0,
    opportunities: cardsByKey.opportunities ?? 0,
  };

  const quickItems = visibleGroups.flatMap((group) => group.items).slice(0, 8);
  const commandResults = deferredQuery.length >= 2 ? search.data ?? [] : quickItems.map((item) => ({
    id: item.href,
    kind: "Kısayol",
    title: item.label,
    path: item.href,
  }));

  function toggleGroup(id: string) {
    setCollapsedGroups((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      window.localStorage.setItem(preferenceKey, JSON.stringify([...next]));
      return next;
    });
  }

  function toggleSidebar() {
    setSidebarCollapsed((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(
          `ipf-panel-sidebar:${user.id}`,
          next ? "collapsed" : "expanded",
        );
      } catch {
        // The current session still keeps the selected layout.
      }
      return next;
    });
  }

  function chooseCommand(path: string) {
    setPaletteOpen(false);
    setQuery("");
    setActiveResult(0);
    navigate(path);
  }

  async function logout() {
    try {
      await apiRequest("auth/logout/", z.object({ stage: z.string() }), { method: "POST" });
      resetCsrf();
      navigate("/giris", { replace: true });
    } catch {
      notify({
        title: "Oturum kapatılamadı",
        description: "Bağlantınızı kontrol edip yeniden deneyin.",
        tone: "danger",
      });
    }
  }

  const renderNavigation = (idPrefix: string) => (
    <SidebarNavigation
      pathname={pathname}
      groups={visibleGroups}
      collapsedGroups={collapsedGroups}
      badgeValues={badgeValues}
      idPrefix={idPrefix}
      onToggleGroup={toggleGroup}
      onNavigate={() => setMobileMenuOpen(false)}
    />
  );

  return (
    <div className={`app-shell ${sidebarCollapsed ? "is-sidebar-collapsed" : ""}`}>
      <a className="skip-link" href="#panel-content">Ana içeriğe geç</a>
      <aside className="sidebar desktop-sidebar" aria-label="Ana menü">
        <SidebarBrand />
        {renderNavigation("desktop")}
        <Tooltip
          label={sidebarCollapsed ? "Menüyü genişlet" : "Menüyü daralt"}
          side="right"
        >
          <button
            type="button"
            className="sidebar-collapse-control"
            aria-label={sidebarCollapsed ? "Menüyü genişlet" : "Menüyü daralt"}
            aria-expanded={!sidebarCollapsed}
            aria-controls="desktop-panel-navigation"
            onClick={toggleSidebar}
          >
            {sidebarCollapsed ? (
              <PanelLeftOpen size={18} aria-hidden="true" />
            ) : (
              <PanelLeftClose size={18} aria-hidden="true" />
            )}
            <span className="sidebar-collapse-label">Menüyü daralt</span>
          </button>
        </Tooltip>
        <div className="sidebar-user-summary">
          <span className="user-avatar" aria-hidden="true">{initials(user.displayName)}</span>
          <div><strong>{user.displayName}</strong><span>{user.role}</span></div>
        </div>
      </aside>

      <Drawer
        open={mobileMenuOpen}
        onOpenChange={setMobileMenuOpen}
        title="Panel menüsü"
      >
        <div className="mobile-sidebar">
          <SidebarBrand onClose={() => setMobileMenuOpen(false)} />
          {renderNavigation("mobile")}
          <div className="sidebar-user-summary">
            <span className="user-avatar" aria-hidden="true">{initials(user.displayName)}</span>
            <div><strong>{user.displayName}</strong><span>{user.role}</span></div>
          </div>
        </div>
      </Drawer>

      <div className="main-column" id="panel-content" tabIndex={-1}>
        <header className="topbar">
          <IconButton
            label="Menüyü aç"
            className="mobile-menu-trigger"
            onClick={() => setMobileMenuOpen(true)}
          >
            <Menu size={19} />
          </IconButton>
          <div className="topbar-context">
            <Breadcrumbs items={getBreadcrumbs(pathname)} />
            <button className="command-trigger" aria-label="Panelde ara veya komut çalıştır" onClick={() => setPaletteOpen(true)}>
              <Search size={17} aria-hidden="true" />
              <span>Panelde ara veya komut çalıştır…</span>
              <kbd><span>⌘</span>K</kbd>
            </button>
          </div>
          <div className="top-actions">
            <Tooltip label={theme === "dark" ? "Açık temaya geç" : "Koyu temaya geç"} side="bottom">
              <IconButton
                label={theme === "dark" ? "Açık temaya geç" : "Koyu temaya geç"}
                className="theme-toggle"
                onClick={() => setTheme((current) => current === "dark" ? "light" : "dark")}
              >
                {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
              </IconButton>
            </Tooltip>
            <Tooltip label="Bildirimler" side="bottom">
              <Link className="ui-icon-link notification-link" to="/bildirimler" aria-label="Bildirimler">
                <Bell size={18} />
                {!!overview.data?.unreadNotifications && (
                  <span className="notification-count">
                    {overview.data.unreadNotifications > 99 ? "99+" : overview.data.unreadNotifications}
                  </span>
                )}
              </Link>
            </Tooltip>
            <DropdownMenu
              label="Kullanıcı menüsü"
              trigger={
                <button className="user-menu-trigger" aria-label="Kullanıcı menüsünü aç">
                  <span className="user-avatar">{initials(user.displayName)}</span>
                  <span className="user-menu-copy"><strong>{user.displayName}</strong><small>{user.role}</small></span>
                  <ChevronDown size={15} aria-hidden="true" />
                </button>
              }
            >
              <DropdownLabel className="ui-dropdown-profile">
                <strong>{user.displayName}</strong><span>{user.email}</span>
              </DropdownLabel>
              <DropdownSeparator className="ui-dropdown-separator" />
              {user.capabilities.has("settings.manage") && (
                <DropdownItem className="ui-dropdown-item" onSelect={() => navigate("/ayarlar")}>
                  <Settings size={16} />Ayarlar
                </DropdownItem>
              )}
              <DropdownItem className="ui-dropdown-item" onSelect={() => void logout()}>
                <LogOut size={16} />Çıkış yap
              </DropdownItem>
            </DropdownMenu>
          </div>
        </header>
        {children}
      </div>

      <Dialog
        open={paletteOpen}
        onOpenChange={(next) => {
          setPaletteOpen(next);
          if (!next) {
            setQuery("");
            setActiveResult(0);
          }
        }}
        title="Panelde ara"
        description="İçerik, üye, fırsat veya başvuruya hızlıca ulaşın."
        size="lg"
      >
        <div className="command-palette">
          <div className="command-palette-input">
            <Search size={19} aria-hidden="true" />
            <input
              autoFocus
              autoComplete="off"
              name="panel-search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveResult(0);
              }}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  setActiveResult((current) => Math.min(current + 1, commandResults.length - 1));
                }
                if (event.key === "ArrowUp") {
                  event.preventDefault();
                  setActiveResult((current) => Math.max(0, current - 1));
                }
                if (event.key === "Enter" && commandResults[activeResult]) {
                  event.preventDefault();
                  chooseCommand(commandResults[activeResult].path);
                }
              }}
              placeholder="Aramaya başlayın…"
              aria-label="Panelde ara"
              aria-controls="command-results"
            />
            {query && (
              <IconButton label="Aramayı temizle" variant="ghost" onClick={() => {
                setQuery("");
                setActiveResult(0);
              }}>
                <X size={16} />
              </IconButton>
            )}
          </div>
          <div className="command-palette-section-label">
            {deferredQuery.length >= 2 ? "Arama sonuçları" : "Hızlı erişim"}
          </div>
          <div className="command-results" id="command-results" role="listbox">
            {search.isLoading && deferredQuery.length >= 2 && <Skeleton lines={3} />}
            {!search.isLoading && commandResults.map((result, index) => (
              <button
                key={`${result.kind}-${result.id}`}
                className={`command-result ${index === activeResult ? "is-active" : ""}`}
                role="option"
                aria-selected={index === activeResult}
                onMouseEnter={() => setActiveResult(index)}
                onClick={() => chooseCommand(result.path)}
              >
                <span className="command-result-icon"><FileText size={17} /></span>
                <span><strong>{result.title}</strong><small>{result.kind}</small></span>
                <ChevronRight size={16} aria-hidden="true" />
              </button>
            ))}
            {deferredQuery.length === 1 && (
              <div className="command-message">Arama için en az iki karakter girin.</div>
            )}
            {deferredQuery.length >= 2 && !search.isLoading && commandResults.length === 0 && (
              <div className="command-message">Bu aramayla eşleşen kayıt bulunamadı.</div>
            )}
            {search.isError && (
              <div className="command-message command-error">Arama şu anda kullanılamıyor.</div>
            )}
          </div>
          <footer className="command-footer">
            <span><kbd>↑</kbd><kbd>↓</kbd> Gezin</span>
            <span><kbd>Enter</kbd> Aç</span>
            <span><kbd>Esc</kbd> Kapat</span>
          </footer>
        </div>
      </Dialog>
    </div>
  );
}
